import type { ReactNode } from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import { ListItemContent, MediaThumbnailStack } from '@frameio/vapor';
import { IconShare } from '@frameio/vapor-icons';
import { theme } from '@frameio/vapor-theme';

export const ThumbnailWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  position: relative;
`;

export const SkeletonThumbnail = styled.div`
  align-items: center;
  border-radius: inherit;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

type ShareSkeletonThumbnailProps = {
  backgroundColor?: string;
  borderColor?: string;
  icon?: ReactNode;
  thumbnailCount?: 1 | 2 | 3;
};

export const ShareSkeletonThumbnail = ({
  backgroundColor = theme.colors.background.primary,
  borderColor = theme.colors.border.neutral,
  icon = <IconShare color={theme.colors.icon.interactive.off} size={16} />,
  thumbnailCount = 1,
}: ShareSkeletonThumbnailProps) => {
  const assets = useMemo(
    () =>
      Array.from({ length: thumbnailCount }, (_, i) => ({ id: String(i + 1) })),
    [thumbnailCount]
  );

  return (
    <MediaThumbnailStack
      assets={assets}
      mode="fill"
      renderThumb={(_, options) => (
        <SkeletonThumbnail
          style={{
            ...options.style,
            backgroundColor,
            border: `.5px solid ${borderColor}`,
          }}
        >
          {icon}
        </SkeletonThumbnail>
      )}
      size="xsmall"
    />
  );
};

export const ShareNavItemContent = styled(ListItemContent)<{
  $isActive: boolean;
  $isFocused?: boolean;
}>`
  align-items: center;
  border-radius: ${theme.radii[8]};
  display: flex;
  height: ${theme.componentSizes[48]};
  min-width: 0;
  padding: ${theme.spaces[4]} ${theme.spaces[6]};
  transition:
    background-color ${theme.transitions.eases.primary[150]}
      ${theme.transitions.eases.primary},
    box-shadow ${theme.transitions.eases.primary[150]}
      ${theme.transitions.eases.primary};

  ${(props) =>
    props.$isActive
      ? `
    background-color: ${theme.colors.background.interactive.selected};
    ${
      props.$isFocused
        ? `box-shadow: inset 0 0 0 1px ${theme.colors.border.interactive.focused};`
        : ''
    }
  `
      : `
    &:hover {
      background-color: ${theme.colors.background.interactive.hover};
    }
    ${
      props.$isFocused
        ? `
      background-color: ${theme.colors.background.interactive.hover};
      box-shadow: inset 0 0 0 1px ${theme.colors.border.interactive.focused};
    `
        : ''
    }
  `}
`;
